<template>
  <div class="px-2">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9E6D2B"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#9E6D2B" right :timeout="timeout">
      <span style="color: #FFFFFF; font-weight: 600;">{{ msg }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="showsnackbar = false">
          <v-icon style="color: #FFFFFF">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- TOP HEADER & CONTROLS -->
    <v-row align="center" justify="space-between" class="mb-3">
      <v-col cols="12" sm="6" md="6" class="py-1">
        <span class="mainHeader">Creditors' Report</span>
      </v-col>
      <v-col cols="12" sm="6" md="6" class="d-flex align-center justify-end py-1">
        <v-btn
          color="#9E6D2B"
          dark
          height="40"
          elevation="1"
          class="text-none font-weight-bold px-4"
          style="border-radius: 8px;"
          @click="download"
          :disabled="appLoading"
        >
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Download PDF
        </v-btn>
      </v-col>
    </v-row>

    <!-- PRO KPI METRIC CARDS -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4" md="4" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">TOTAL CREDITORS</span>
            <v-icon small color="#9E6D2B">mdi-account-group-outline</v-icon>
          </div>
          <div class="kpi-val">{{ filteredCreditors.length }} <small class="text-caption">Suppliers</small></div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4" md="4" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">TOTAL BALANCE AMOUNT</span>
            <v-icon small color="#047857">mdi-cash-multiple</v-icon>
          </div>
          <div class="kpi-val text-green">₹{{ formatCurrency(computedTotalAmount) }}</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4" md="4" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">TOTAL BALANCE WEIGHT</span>
            <v-icon small color="#2563EB">mdi-scale-balance</v-icon>
          </div>
          <div class="kpi-val text-blue">{{ formatWeight(computedTotalWeight) }} <small class="text-caption">g</small></div>
        </v-card>
      </v-col>
    </v-row>

    <!-- FILTER & SEARCH BAR -->
    <div class="filter-search-wrap mb-4">
      <div class="search-input-wrap" style="width: 100%; max-width: 320px;">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search supplier name..."
          outlined
          dense
          hide-details
          clearable
          class="search-field"
        ></v-text-field>
      </div>
    </div>

    <!-- NO DATA STATE -->
    <div v-if="!appLoading && filteredCreditors.length === 0" class="no-data-box text-center py-8 my-4">
      <v-icon color="#9E6D2B" large class="mb-2">mdi-account-off-outline</v-icon>
      <div class="no-data-text">No creditors found or no balance data available.</div>
    </div>

    <!-- MAIN CREDITORS REPORT TABLE CARD -->
    <v-card v-if="filteredCreditors.length > 0" outlined class="rounded-lg overflow-hidden elevation-1 mb-5" color="#FFFFFF">
      <div class="table-container">
        <table class="pro-creditors-table">
          <thead>
            <tr>
              <th class="text-left sticky-col">SUPPLIER NAME</th>
              <th class="text-right">BALANCE AMOUNT (₹)</th>
              <th class="text-right">BALANCE WEIGHT (g)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rIdx) in filteredCreditors" :key="rIdx" class="table-row">
              <td class="text-left sticky-col font-weight-bold">
                <div class="d-flex align-center">
                  <v-avatar color="#F5EBDC" size="28" class="mr-2">
                    <span class="supplier-avatar-text">{{ (row.supplier || 'S').charAt(0).toUpperCase() }}</span>
                  </v-avatar>
                  <span>{{ row.supplier || '-' }}</span>
                </div>
              </td>
              <td class="text-right font-weight-bold gold-amount-text">
                ₹{{ formatCurrency(row.balanceAmount) }}
              </td>
              <td class="text-right font-weight-semibold">
                {{ formatWeight(row.balanceWeight) }} g
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="totals-row">
              <td class="sticky-col total-label-cell">TOTAL</td>
              <td class="text-right total-amount-cell">
                <strong>₹{{ formatCurrency(computedTotalAmount) }}</strong>
              </td>
              <td class="text-right total-weight-cell">
                <strong>{{ formatWeight(computedTotalWeight) }} g</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "CreditorsReportPage",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 3000,
      msg: null,
      creditorRows: [],
      totals: { amount: 0, weight: 0 },
      searchQuery: "",
    };
  },
  computed: {
    filteredCreditors() {
      let result = this.creditorRows;
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        result = result.filter(r => (r.supplier || "").toLowerCase().includes(q));
      }
      return result;
    },
    computedTotalAmount() {
      return this.filteredCreditors.reduce((acc, curr) => acc + (Number(curr.balanceAmount) || 0), 0);
    },
    computedTotalWeight() {
      return this.filteredCreditors.reduce((acc, curr) => acc + (Number(curr.balanceWeight) || 0), 0);
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    formatCurrency(val) {
      const num = Number(val) || 0;
      return num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatWeight(val) {
      const num = Number(val) || 0;
      return num.toLocaleString("en-IN", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      });
    },
    getList() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/data/creditorsreport",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status) {
            this.creditorRows = response.data.data.creditorRows || [];
            this.totals = response.data.data.totals || { amount: 0, weight: 0 };
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to fetch creditors report";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    download() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/creditorsreport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "CreditorsReport.pdf");
          document.body.appendChild(link);
          link.click();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.mainHeader {
  font-family: 'Cinzel', serif;
  font-size: 22px;
  font-weight: 700;
  color: #1E1815;
  letter-spacing: 0.3px;
}

/* KPI CARDS */
.kpi-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #8C7D6F;
  text-transform: uppercase;
}

.kpi-val {
  font-size: 18px;
  font-weight: 800;
  color: #1E1815;
}

.text-blue { color: #2563EB; }
.text-green { color: #047857; }

/* FILTER & SEARCH */
.filter-search-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: #FFFFFF;
  padding: 10px 14px;
  border: 1px solid #EFE8DD;
  border-radius: 12px;
}

.no-data-box {
  background: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #8C7D6F;
  font-weight: 500;
}

/* TABLE STYLING */
.table-container {
  overflow-x: auto;
  width: 100%;
}

.pro-creditors-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.pro-creditors-table th {
  background: #FAF8F5;
  color: #6B5738;
  font-size: 11.5px;
  font-weight: 700;
  padding: 10px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #EFE8DD;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.pro-creditors-table td {
  padding: 11px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #F3EFE8;
  white-space: nowrap;
  color: #1E1815;
}

.table-row:hover td {
  background-color: #FAF5EA !important;
}

.table-row:hover .sticky-col {
  background-color: #FAF5EA !important;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #FFFFFF !important;
  border-right: 2px solid #EFE8DD !important;
  min-width: 180px;
}

th.sticky-col {
  background: #FAF8F5 !important;
  color: #6B5738 !important;
}

.supplier-avatar-text {
  font-size: 12px;
  font-weight: 700;
  color: #7D551E;
}

.gold-amount-text {
  color: #9E6D2B;
}

.totals-row td {
  background-color: #F7F2E8 !important;
  border-top: 2px solid #9E6D2B !important;
  border-bottom: none !important;
  color: #1E1815;
  font-weight: 700;
  padding: 12px 16px;
}

.total-label-cell {
  color: #9E6D2B !important;
  font-family: 'Cinzel', serif;
  font-size: 13.5px !important;
  background-color: #F7F2E8 !important;
}

.total-amount-cell {
  color: #9E6D2B !important;
  font-size: 14.5px;
}

.total-weight-cell {
  color: #1E1815 !important;
  font-size: 14.5px;
}
</style>
